import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { useConfirm } from '../components/ui';
import {
  Database,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  Trash2,
  Save,
  Download,
  Upload
} from 'lucide-react';
import './SettingsPage.css';

interface DatabaseStats {
  utenti: number;
  gusti: number;
  ingredienti: number;
  punti_vendita: number;
  categorie_gusti: number;
  allergeni: number;
  produzione_totale: number;
  produzione_ultimo_mese: number;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();

  // Database stats state
  const [stats, setStats] = useState<DatabaseStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Reset database
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);

  // Import backup state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importFeedback, setImportFeedback] = useState<any | null>(null);
  const [showImportForm, setShowImportForm] = useState(false);

  useEffect(() => {
    if (user?.ruolo === 'admin') {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const statsData = await apiService.getAdminStats();
      setStats(statsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento delle statistiche');
      console.error('Errore:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetDatabase = async () => {
    if (!resetPassword) {
      setError('Inserisci la password di sicurezza');
      return;
    }

    const ok = await confirm({
      title: 'Reset database',
      variant: 'danger',
      message: (
        <>
          <p>Questa operazione elimina tutti i dati del database.</p>
          <p>L'operazione non può essere annullata.</p>
        </>
      ),
      confirmLabel: 'Reset database',
    });
    if (!ok) {
      return;
    }

    try {
      setResetLoading(true);
      setError(null);

      console.log('🔄 Avvio reset database...');
      const response = await apiService.resetDatabase(resetPassword);
      console.log('✅ Reset completato:', response);
      
      // Dopo il reset, ricarica tutti i dati
      await loadStats();
      
      setResetSuccess(true);
      setResetPassword('');
      setShowResetForm(false);
      
      setTimeout(() => {
        setResetSuccess(false);
      }, 5000);
    } catch (err: any) {
      console.error('❌ Errore durante il reset:', err);
      setError(err.message || 'Errore durante il reset del database');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const response = await apiService.downloadBackup();
      
      // Crea un blob dal contenuto JSON
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      
      // Crea un link per il download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().split('T')[0]}.json`;
      
      // Avvia il download
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Libera la memoria
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(err.message || 'Errore durante il download del backup');
      console.error('Errore:', err);
    }
  };

  const handleImportBackup = async () => {
    if (!importFile) {
      setError('Seleziona un file di backup');
      return;
    }

    try {
      setImportLoading(true);
      setError(null);
      setImportFeedback(null);
      
      const response = await apiService.importBackup(importFile);
      
      if (response.success) {
        setImportSuccess(true);
        setImportFeedback(response);
        setImportFile(null);
        setShowImportForm(false);
        
        setTimeout(() => {
          if (importSuccess) {
            setImportSuccess(false);
            setImportFeedback(null);
          }
        }, 15000);
      } else {
        setImportSuccess(false);
        setImportFeedback(response);
        setError(response.message || 'Errore durante l\'importazione del backup');
      }
      
      console.log('🔄 Ricaricamento dati interfaccia...');
      await loadStats();
      console.log('✅ Dati interfaccia ricaricati');
      
      setTimeout(() => {
        if (importSuccess) {
          setImportSuccess(false);
          setImportFeedback(null);
        }
      }, 15000);
    } catch (err: any) {
      console.error('❌ Errore durante l\'importazione:', err);
      console.error('🔍 Stack trace completo:', err.stack);
      setError(err.message || 'Errore durante l\'importazione del backup');
    } finally {
      setImportLoading(false);
      console.log('🏁 Processo di importazione terminato');
    }
  };

  if (user?.ruolo !== 'admin') {
    return (
      <div className="settings-page">
        <div className="access-denied">
          <h2>Accesso Negato</h2>
          <p>Solo gli amministratori possono accedere alle impostazioni avanzate.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1>
        <Database size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
        Impostazioni Database
      </h1>
      
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}
      
      {resetSuccess && (
        <div className="alert alert-success">
          Database resettato
        </div>
      )}

      {/* Database Statistics */}
      <div className="settings-section">
        <div className="section-header">
          <h2>
            <BarChart3 size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            Statistiche Database
          </h2>
          <button
            className="btn btn-outline-primary"
            onClick={() => loadStats()}
            disabled={loading}
          >
            <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            {loading ? 'Caricamento...' : 'Aggiorna'}
          </button>
        </div>
        
        <div className="stats-grid">
          {loading ? (
            <div className="loading">Caricamento statistiche...</div>
          ) : stats ? (
            <div className="stats-cards">
              <div className="stat-card">
                <h3>Utenti</h3>
                <p className="stat-value">{stats.utenti}</p>
              </div>
              <div className="stat-card">
                <h3>Gusti</h3>
                <p className="stat-value">{stats.gusti}</p>
              </div>
              <div className="stat-card">
                <h3>Ingredienti</h3>
                <p className="stat-value">{stats.ingredienti}</p>
              </div>
              <div className="stat-card">
                <h3>Punti Vendita</h3>
                <p className="stat-value">{stats.punti_vendita}</p>
              </div>
              <div className="stat-card">
                <h3>Categorie Gusti</h3>
                <p className="stat-value">{stats.categorie_gusti}</p>
              </div>
              <div className="stat-card">
                <h3>Allergeni</h3>
                <p className="stat-value">{stats.allergeni}</p>
              </div>
              <div className="stat-card">
                <h3>Produzione Totale</h3>
                <p className="stat-value">{stats.produzione_totale}</p>
              </div>
              <div className="stat-card">
                <h3>Produzione Ultimo Mese</h3>
                <p className="stat-value">{stats.produzione_ultimo_mese}</p>
              </div>
            </div>
          ) : (
            <div className="error-message">Errore nel caricamento delle statistiche</div>
          )}
        </div>
      </div>

      {/* Reset Database */}
      <div className="settings-section danger-section">
        <h2>
          <AlertTriangle size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Reset Database
        </h2>
        <p className="danger-text">
          <strong>Attenzione:</strong> questa operazione eliminerà tutti i dati del database
          (ingredienti, gusti, punti vendita, storico produzioni).
          L'operazione è irreversibile.
        </p>
        
        {!showResetForm ? (
          <button 
            className="btn btn-danger"
            onClick={() => setShowResetForm(true)}
          >
            Mostra Reset Database
          </button>
        ) : (
          <div className="reset-form">
            <div className="form-group">
              <label>Password di sicurezza:</label>
              <input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Inserisci la password di sicurezza"
                className="form-control"
              />
            </div>
            
            <div className="form-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowResetForm(false);
                  setResetPassword('');
                }}
              >
                Annulla
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleResetDatabase}
                disabled={resetLoading || !resetPassword}
              >
                <Trash2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                {resetLoading ? 'Reset in corso...' : 'Reset database'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Backup Database */}
      <div className="settings-section">
        <h2>
          <Save size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Gestione Backup Database
        </h2>
        
        <div className="backup-grid">
          {/* Download Backup */}
          <div className="backup-card">
            <h3>Scarica Backup</h3>
            <p>Scarica un backup completo del database in formato JSON</p>
            <button
              className="btn btn-primary"
              onClick={handleDownloadBackup}
            >
              <Download size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
              Scarica Backup
            </button>
          </div>

          {/* Import Backup */}
          <div className="backup-card">
            <h3>Importa Backup</h3>
            <p>Carica un file di backup per ripristinare il database</p>
            
            {!showImportForm ? (
              <button 
                className="btn btn-secondary"
                onClick={() => setShowImportForm(true)}
              >
                Mostra Importa Backup
              </button>
            ) : (
              <div className="import-form">
                <div className="form-group">
                  <label>Seleziona file di backup:</label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="form-control"
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowImportForm(false);
                      setImportFile(null);
                    }}
                  >
                    Annulla
                  </button>
                  <button 
                    className="btn btn-warning"
                    onClick={handleImportBackup}
                    disabled={importLoading || !importFile}
                  >
                    <Upload size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
                    {importLoading ? 'Importazione...' : 'Importa Backup'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Feedback Messages */}
        {importSuccess && importFeedback && (
          <div className="alert alert-success" style={{ whiteSpace: 'pre-line', marginTop: '20px' }}>
            <b>Backup importato</b><br/>
            {importFeedback.message && <div>{importFeedback.message}</div>}
            {importFeedback.backupInfo && (
              <div style={{ fontSize: '0.95em', margin: '0.5em 0' }}>
                <b>File:</b> {importFeedback.backupInfo.filename}<br/>
                <b>Versione:</b> {importFeedback.backupInfo.version}<br/>
                <b>Data backup:</b> {importFeedback.backupInfo.timestamp}
              </div>
            )}
            {importFeedback.importCounts && (
              <div style={{ fontSize: '0.95em', margin: '0.5em 0' }}>
                <b>Record importati:</b>
                <ul style={{ margin: 0 }}>
                  {Object.entries(importFeedback.importCounts).map(([tab, count]) => (
                    <li key={tab}>{tab}: {String(count)}</li>
                  ))}
                </ul>
              </div>
            )}
            {importFeedback.warnings && importFeedback.warnings.length > 0 && (
              <div style={{ color: '#b88600', margin: '0.5em 0' }}>
                <b>Avvisi:</b>
                <ul style={{ margin: 0 }}>
                  {importFeedback.warnings.map((w: string, i: number) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        
        {importFeedback && !importSuccess && (
          <div className="alert alert-danger" style={{ whiteSpace: 'pre-line', marginTop: '20px' }}>
            <b>Errore durante l'importazione</b><br/>
            {importFeedback.message && <div>{importFeedback.message}</div>}
            {importFeedback.type && <div><b>Tipo errore:</b> {importFeedback.type}</div>}
            {importFeedback.details && <div><b>Dettagli:</b> {importFeedback.details}</div>}
            
            {importFeedback.actualDbStats && (
              <div style={{ backgroundColor: '#fff3cd', padding: '10px', margin: '10px 0', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                <b>Statistiche attuali del database:</b>
                <ul style={{ margin: '5px 0' }}>
                  <li>Utenti: {importFeedback.actualDbStats.utenti}</li>
                  <li>Gusti: {importFeedback.actualDbStats.gusti}</li>
                  <li>Ingredienti: {importFeedback.actualDbStats.ingredienti}</li>
                  <li>Punti Vendita: {importFeedback.actualDbStats.punti_vendita}</li>
                  <li>Produzioni: {importFeedback.actualDbStats.produzione_totale}</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
